<template>
  <div>
    <div
      data-test="smart-search-bar"
      class="smart-search-bar"
      :class="{ 'query-error': exceededQueryLength }"
    >
      <Tooltip
        text="Gyre Recommends is a BETA feature, meaning we are still ironing out any rough edges"
        :enterable="false"
      >
        <img class="beta-sticker" src="../../assets/beta-sticker.svg" alt="Gyre BETA sticker" />
      </Tooltip>
      <div class="smart-search-bar-paper">
        <textarea
          data-test="smart-search-sample-question"
          ref="sampleQueryTextAreaRef"
          class="smart-search-bar-textarea smart-search-bar-textarea-watermark"
          :class="{ hidden: query || hasFocus }"
          rows="1"
          disabled="true"
          v-model="sampleQuestion"
          aria-hidden
        />
        <textarea
          data-test="smart-search-question"
          ref="queryTextAreaRef"
          class="smart-search-bar-textarea smart-search-bar-user-textarea"
          rows="1"
          v-model="query"
          @input="input"
          @blur="onBlur"
          @keydown="onKeyDown($event)"
          @change="queryChanged"
          aria-label="Enter your question here"
        />
        <p v-if="query.length" class="character-count">{{ characterCount }} / 1500</p>
      </div>

      <div class="smart-search-bar-button-container">
        <button
          data-test="smart-search-button"
          class="btn smart-search-bar-button"
          :class="{ 'btn-default': !active, 'btn-primary': active }"
          :disabled="!active"
          @click="run()"
          type="button"
        >
          <img src="../../assets/smart-search/search-icon.svg" alt="Search" />
        </button>
      </div>
    </div>
    <p class="text-danger text-center error-message" v-if="exceededQueryLength">
      We appreciate your enthusiasm, but this field has a character limit. Please reduce your input
      and try again. Characters used: <span>{{ characterCount }} / 1500</span>
    </p>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable */
import SearchResult from "./smart-search-result";
import { processSituations } from "./process-situations";
import { CmsDocumentTypes } from "../../services/cms/cms-type-configuration";
import CmsContentMap from "../../services/cms/cms-content-map";
import cms from "../../services/cms/cms";
import { Tooltip } from "uiv";
import { ref, computed, onMounted } from "vue";

window["processSituations"] = processSituations;

const props = withDefaults(
  defineProps<{
    question: string | null;
  }>(),
  {
    question: null,
  },
);

const emit = defineEmits(["run"]);

// template refs
const queryTextAreaRef = ref<HTMLTextAreaElement | null>(null);
const sampleQueryTextAreaRef = ref<HTMLTextAreaElement | null>(null);

const hasFocus = ref(false);
const query = computed(() => props.question || window["_jyreQuery"] || "");
const lastQuery = ref<string | null>(null);

const sampleQuestion = ref("");
const exceededQueryLength = ref(false);

function updateTextAreaHeight(): void {
  const queryTextArea = queryTextAreaRef.value as HTMLTextAreaElement;
  const sampleQueryTextArea = sampleQueryTextAreaRef.value as HTMLTextAreaElement;
  queryTextArea.style.height = "0";
  sampleQueryTextArea.style.height = "0";

  const scrollHeight = Math.max(queryTextArea.scrollHeight, sampleQueryTextArea.scrollHeight);
  queryTextArea.style.height = scrollHeight + "px";
  sampleQueryTextArea.style.height = scrollHeight + "px";
}

function input() {
  updateTextAreaHeight();
}
const characterCount = computed(() => {
  return query.value.length;
});

const active = computed(() => {
  return (
    query.value !== lastQuery.value &&
    (query.value.trim() || (!hasFocus.value && !query.value.trim()))
  );
});

function queryChanged(): void {
  window["_jyreQuery"] = query.value;
  if (query.value.length > 1500) {
    exceededQueryLength.value = true;
  } else if (exceededQueryLength.value) {
    exceededQueryLength.value = false;
  }
}

function onKeyDown(event: KeyboardEvent): void {
  if (event.key === "Enter") {
    event.preventDefault();
    run();
  }
  hasFocus.value = true;
}

function onBlur(): void {
  hasFocus.value = false;
  setTimeout(updateTextAreaHeight, 10);
  exceededQueryLength.value = false;
}

const results = ref<SearchResult[]>(window["_jyreSearchResults"] || []);

function run() {
  if (query.value.length <= 1500) {
    const sanitizedQuery = query.value ? query.value.replace(/(\r\n|\n|\r)/gm, " ") : null;
    emit("run", sanitizedQuery || sampleQuestion.value);
  } else {
    exceededQueryLength.value = true;
  }
}

async function setSampleQuestion(): Promise<void> {
  const doc = await cms.getByUid(
    CmsDocumentTypes.CmsSmartSearchPrompts,
    CmsContentMap.smartSearch.promptsUid,
  );

  const sampleQuestions = doc?.data.prompts.split("\n");
  sampleQuestion.value = sampleQuestions[Math.floor(Math.random() * sampleQuestions.length)].trim();
}

onMounted(async () => {
  await setSampleQuestion();
  setTimeout(updateTextAreaHeight, 100);
  addEventListener("resize", updateTextAreaHeight);
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less" scoped>
@import (reference) "../../styles/bootstrap/variables.less";

.smart-search-bar-textarea {
  border: none;
  overflow: hidden;
  outline: none;
  width: 100%;
  background: none;
  line-height: 27px;
  margin: 0;
  padding-left: 10px;
  padding-top: 12px;

  resize: none; /*remove the resize handle on the bottom right*/
}

//https://www.w3schools.com/css/css_temp_tooltip.asp
.smart-search-bar {
  max-width: 600px;
  margin: 0 auto;
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  background-color: #fff;
  border: 1px #ddd solid;
  position: relative;
}

.smart-search-bar-button-container {
  padding: 5px 12px 5px;
}

.smart-search-bar-button {
  min-height: 40px;
  min-width: 40px;
  border-radius: 50%;
  padding: 2px;
}

.smart-search-bar-paper {
  position: relative;
  margin-left: 45px;
  width: 100%;
}

.smart-search-bar-user-textarea {
  z-index: 1;
  position: relative;
}

.smart-search-bar-textarea-watermark {
  position: absolute;
  background: #fff;
  color: #aaa;
}

.beta-sticker {
  position: absolute;
  cursor: help;
  z-index: 1;
  width: 70px;
}

.error-message {
  max-width: 600px;
  margin: 0 auto;
}

.query-error {
  border-color: @brand-danger;
}

.character-count {
  color: @form-text-color;
  padding-left: 10px;
  font-size: 12px;
}
</style>
